import { useEffect } from 'react';

import type { LogsEvent, LogsEventDomainContext } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';
import type { RumEvent, RumEventDomainContext } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import type { LDClient, LDFlagSet } from 'launchdarkly-react-client-sdk';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { z } from 'zod';

import Constants from '@/constants';

import { OktaUserRaw } from '@/types';

import { useFeaturePermission } from './permissions';

type DatadogEvent = RumEvent | LogsEvent;

type DatadogEventContext =
  | (RumEventDomainContext & {
      isAborted?: unknown;
      aborted?: unknown;
      handlingStack?: unknown;
    })
  | (LogsEventDomainContext & {
      isAborted?: unknown;
      aborted?: unknown;
      handlingStack?: unknown;
    });

const asDatadogEvent = (value: unknown): DatadogEvent =>
  typeof value === 'object' && value !== null
    ? (value as DatadogEvent)
    : ({} as DatadogEvent);

const asDatadogEventContext = (value: unknown): DatadogEventContext =>
  typeof value === 'object' && value !== null
    ? (value as DatadogEventContext)
    : {};

const FIXED_DATADOG_CONTEXT = {
  system_tag: 'pathway',
  team: 'OTS - Pathway 2.0',
  sub_dept: 'OTS',
  org_id: '221067',
} as const;

const ABORT_ERROR_NAMES = new Set(['aborterror', 'cancelederror']);
const ABORT_MESSAGE_SIGNATURES = [
  'fetch is aborted',
  'signal is aborted',
  'aborted without reason',
];

// Suppress heartbeat-scoped transport noise while preserving real failures
const LOAD_FAILED_NOISE_MESSAGES = [
  'load failed',
  'failed to fetch',
  'networkerror when attempting to fetch resource',
  'fetch is aborted',
  'signal is aborted without reason',
];

const HEARTBEAT_HINTS = [
  '/heartbeat',
  'heartbeat',
  'usetrainingplantimer',
  'usetrainingheartbeatplantimer',
  'usecompliancetimer',
];

const REAUTHENTICATE_REDIRECT_MESSAGE = 'redirecting to reauthenticate';

// Suppress browser intervention warnings from smooth-dnd attempting to
// preventDefault on passive touchmove listeners during scroll
const IGNORED_TOUCHMOVE_INTERVENTION_MESSAGES = [
  'ignored attempt to cancel a touchmove event with cancelable=false',
  'unable to preventdefault inside passive event listener',
  'smooth-dnd cannot cancel touchmove during scroll',
];

const includesAny = (haystack: string, needles: string[]): boolean =>
  needles.some(needle => haystack.includes(needle));

const includesIgnoredTouchmoveIntervention = (value: unknown): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  const message = value.toLowerCase();
  return IGNORED_TOUCHMOVE_INTERVENTION_MESSAGES.some(signature =>
    message.includes(signature),
  );
};

const isIgnoredTouchmoveIntervention = (
  event: DatadogEvent,
  context: DatadogEventContext,
): boolean => {
  if (
    includesIgnoredTouchmoveIntervention(getNestedString(event, ['message']))
  ) {
    return true;
  }

  const contextErrorMessage = getNestedString(context, ['error', 'message']);
  if (includesIgnoredTouchmoveIntervention(contextErrorMessage)) {
    return true;
  }

  const contextErrorStack = getNestedString(context, ['error', 'stack']);
  return includesIgnoredTouchmoveIntervention(contextErrorStack);
};

const isOpaqueScriptErrorNoise = (
  event: DatadogEvent,
  context: DatadogEventContext,
): boolean => {
  const message = [
    getNestedString(event, ['message']),
    getNestedString(event, ['error', 'message']),
    getNestedString(context, ['error', 'message']),
  ]
    .filter(Boolean)
    .join(' | ');

  if (!message.includes('script error')) {
    return false;
  }

  const stack = [
    getNestedString(event, ['error', 'stack']),
    getNestedString(event, ['error', 'stack_trace']),
    getNestedString(context, ['error', 'stack']),
    getNestedString(context, ['error', 'stack_trace']),
    getNestedString(context, ['handlingStack']),
  ]
    .filter(Boolean)
    .join(' | ');

  const filename = [
    getNestedString(event, ['error', 'source']),
    getNestedString(event, ['error', 'file']),
    getNestedString(context, ['error', 'source']),
    getNestedString(context, ['error', 'file']),
  ]
    .filter(Boolean)
    .join(' | ');

  const hasLineOrColumn =
    getNestedValue(event, ['error', 'line']) !== undefined ||
    getNestedValue(event, ['error', 'column']) !== undefined ||
    getNestedValue(context, ['error', 'line']) !== undefined ||
    getNestedValue(context, ['error', 'column']) !== undefined;

  // Browser-masked cross-origin errors often report only "Script error."
  // with no stack, filename, or line/column details.
  return stack === '' && filename === '' && !hasLineOrColumn;
};

const getNestedString = (obj: unknown, path: string[]): string => {
  let current: unknown = obj;
  for (const key of path) {
    if (typeof current !== 'object' || current === null) return '';
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current.toLowerCase() : '';
};

const getNestedValue = (obj: unknown, path: string[]): unknown => {
  let current: unknown = obj;
  for (const key of path) {
    if (typeof current !== 'object' || current === null) return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
};

const isAbortedContext = (context: DatadogEventContext): boolean =>
  context.isAborted === true || context.aborted === true;

const isAbortLikeError = (
  event: DatadogEvent,
  context: DatadogEventContext,
): boolean => {
  const errorNames = [
    getNestedString(context, ['error', 'name']),
    getNestedString(event, ['error', 'name']),
    getNestedString(event, ['error', 'type']),
    getNestedString(event, ['error', 'kind']),
  ].filter(Boolean);

  if (errorNames.some(name => ABORT_ERROR_NAMES.has(name))) {
    return true;
  }

  const messageAndStack = [
    getNestedString(event, ['message']),
    getNestedString(event, ['error', 'message']),
    getNestedString(context, ['error', 'message']),
    getNestedString(event, ['error', 'stack']),
    getNestedString(event, ['error', 'stack_trace']),
  ]
    .filter(Boolean)
    .join(' | ');

  return includesAny(messageAndStack, ABORT_MESSAGE_SIGNATURES);
};

const shouldDropDatadogNoise = (event: unknown, context: unknown): boolean => {
  const normalizedEvent = asDatadogEvent(event);
  const normalizedContext = asDatadogEventContext(context);

  // Preserve existing abort suppression
  if (isAbortedContext(normalizedContext)) {
    return true;
  }

  // Browser intervention warnings from smooth-dnd on passive touchmove listeners
  if (isIgnoredTouchmoveIntervention(normalizedEvent, normalizedContext)) {
    return true;
  }

  // Opaque cross-origin script failures are unactionable without CORS headers.
  if (isOpaqueScriptErrorNoise(normalizedEvent, normalizedContext)) {
    return true;
  }

  // AbortError/CanceledError from TanStack Query cancellations are expected.
  if (isAbortLikeError(normalizedEvent, normalizedContext)) {
    return true;
  }

  // Collect likely message/type/stack/url fields from RUM + Logs payloads
  const message = [
    getNestedString(normalizedEvent, ['error', 'message']),
    getNestedString(normalizedEvent, ['message']),
    getNestedString(normalizedEvent, ['view', 'message']),
  ]
    .filter(Boolean)
    .join(' | ');

  const errorType = [
    getNestedString(normalizedEvent, ['error', 'type']),
    getNestedString(normalizedEvent, ['error', 'kind']),
  ]
    .filter(Boolean)
    .join(' | ');

  const stack = [
    getNestedString(normalizedEvent, ['error', 'stack']),
    getNestedString(normalizedEvent, ['error', 'stack_trace']),
    getNestedString(normalizedContext, ['handlingStack']),
  ]
    .filter(Boolean)
    .join(' | ');

  const url = [
    getNestedString(normalizedEvent, ['view', 'url']),
    getNestedString(normalizedEvent, ['http', 'url']),
    getNestedString(normalizedContext, ['request', 'url']),
    getNestedString(normalizedContext, ['resource', 'url']),
  ]
    .filter(Boolean)
    .join(' | ');

  if (message.includes(REAUTHENTICATE_REDIRECT_MESSAGE)) {
    return true;
  }

  const isNetworkNoiseMessage = includesAny(
    message,
    LOAD_FAILED_NOISE_MESSAGES,
  );
  const isHeartbeatContext =
    includesAny(url, HEARTBEAT_HINTS) || includesAny(stack, HEARTBEAT_HINTS);

  if (!isHeartbeatContext) {
    return false;
  }

  // Keep filter narrow: heartbeat + network transport-like errors only
  const isTransportLikeType =
    errorType.includes('typeerror') ||
    errorType.includes('aborterror') ||
    errorType.includes('authapierror') ||
    errorType === '';

  if (isNetworkNoiseMessage && isTransportLikeType) {
    return true;
  }

  // Session expiry during heartbeat: 401 / token errors are expected while redirecting
  const isAuthNoiseDuringHeartbeat =
    errorType.includes('unauthorizederror') ||
    message.includes('401') ||
    message.includes('unauthorized') ||
    message.includes('no access token');

  return isAuthNoiseDuringHeartbeat;
};

const setupDatadogRum = (user: OktaUserRaw, client: LDClient) => {
  const userId = user['cfa-guid'];
  const userCountry = user['cfa-loc-iso_country'];
  const userAudience = user.cfa_aud;
  const userLocations = user['cfa-locations'];
  const userPermissions = Object.keys(user.cfa_perms.P20);

  const shouldStartDatadog =
    !Constants.IS_LOCAL_DEPLOYMENT &&
    !Constants.IS_IN_CYPRESS_TEST &&
    Constants.DATADOG_APP_ID !== undefined &&
    Constants.DATADOG_CLIENT_TOKEN !== undefined;

  if (!shouldStartDatadog) {
    console.info(`Not starting DD integration...`);
    return;
  }

  const applicationId = Constants.DATADOG_APP_ID;
  const clientToken = Constants.DATADOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) {
    return;
  }

  const flags = client.allFlags();
  const config = getDatadogLDConfig(flags);
  if (!config) return;

  datadogSetupAttempted = true;

  datadogRum.init({
    applicationId,
    beforeSend: (event, context) => !shouldDropDatadogNoise(event, context),
    clientToken,
    site: 'datadoghq.com',
    service: Constants.DATADOG_SERVICE,
    env: Constants.ENV,
    version: Constants.APP_VERSION,
    sessionSampleRate: config.sessionSampleRate,
    sessionReplaySampleRate: config.sessionReplaySampleRate,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'allow',
    allowedTracingUrls: config.allowedTracingUrls,
    traceSampleRate: config.tracingSamplingRate,
    compressIntakeRequests: true,
  });

  datadogLogs.init({
    beforeSend: (event, context) => !shouldDropDatadogNoise(event, context),
    clientToken,
    site: 'datadoghq.com',
    service: Constants.DATADOG_SERVICE,
    env: Constants.ENV,
    version: Constants.APP_VERSION,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['log', 'info', 'warn', 'error'], //"all" or an Array of "log" "debug" "info" "warn" "error"
    sessionSampleRate: config.sessionSampleRate,
    // beforeSend: (log) => { // Discard specific logs. https://docs.datadoghq.com/logs/log_collection/javascript/#discard-specific-logs
    //   // discard 404 network errors
    //   if (log.http && log.http.status_code === 404) {
    //     return false
    //   }
    //   return true
    // },
  });

  datadogRum.setUser({
    id: userId,
    audience: userAudience,
    country: userCountry,
    locations: userLocations,
    permissions: userPermissions,
  });

  datadogLogs.setGlobalContext({
    flags,
    service: Constants.DATADOG_SERVICE,
    userId: userId,
    userAudience: userAudience,
    userCountry: userCountry,
    userLocations: userLocations,
    userPermissions,
    ...FIXED_DATADOG_CONTEXT,
  });

  datadogRum.setGlobalContext({
    flags,
    ...FIXED_DATADOG_CONTEXT,
  });
};

let datadogSetupAttempted = false;

export const DatadogRumSetup: React.FC<{ user?: OktaUserRaw }> = ({ user }) => {
  const rumEnabled = useFeaturePermission('rum');
  const client = useLDClient();

  useEffect(() => {
    if (!user || !rumEnabled || !client || datadogSetupAttempted) {
      return;
    }

    setupDatadogRum(user, client);
  }, [client, rumEnabled, user]);

  return null;
};

const configSchema = z.object({
  allowedTracingUrls: z.array(
    z.object({
      match: z.string(),
      propagatorTypes: z.array(
        z.union([
          z.literal('datadog'),
          z.literal('b3'),
          z.literal('b3multi'),
          z.literal('tracecontext'),
        ]),
      ),
    }),
  ),
  sessionReplaySampleRate: z.number().min(0).max(100),
  sessionSampleRate: z.number().min(0).max(100),
  tracingSamplingRate: z.number().min(0).max(100),
  web: z.object({
    sessionReplaySampleRate: z.number().min(0).max(100),
    sessionSampleRate: z.number().min(0).max(100),
    tracingSamplingRate: z.number().min(0).max(100),
  }),
});

type DatadogLDConfig = z.infer<typeof configSchema>;

const getDatadogLDConfig = (flags: LDFlagSet): DatadogLDConfig | undefined => {
  const configRaw = flags['datadog-config'] as DatadogLDConfig | undefined;

  const configParseAttempt = configSchema.safeParse({
    ...configRaw,
    ...configRaw?.web,
  });

  if (!configParseAttempt.success) {
    datadogRum.addError(
      new Error(
        `LaunchDarkly Datadog config parsing failed: ${configParseAttempt.error}`,
      ),
    );
    return undefined;
  }

  return configParseAttempt.data;
};
